import type { Feed, FeedSyncRunView, WebhookEventView, WebhookStatus, WordfenceStatus } from '@ametica/shared';
import { ApiError } from '../api/client';
import { useReplayWebhookEvent, useResetBreaker, useSyncFeed, useWordfenceStatus } from '../api/wordfence';
import { PageTitle } from '../layout/PageTitle';
import { Icon } from '../components/Icon';
import { useToast } from '../components/Toast';
import { formatDateTime, timeAgo } from '../lib/time';
import { catalogState, formatUtcTimes, quotaSlots, webhookState, type CatalogTone } from '../features/wordfence/status';

const TONE: Record<CatalogTone, string> = {
  ok: 'bg-ok', stale: 'bg-sev-medium', bad: 'bg-sev-critical', empty: 'bg-ghost',
};
const FEED_LABEL: Record<Feed, string> = { PRODUCTION: 'Production feed', SCANNER: 'Scanner feed' };
const TRIGGER_LABEL: Record<FeedSyncRunView['trigger'], string> = { CRON: 'cron', MANUAL: 'ručne', WEBHOOK: 'webhook' };
const nf = new Intl.NumberFormat('sk');

/** Nastavenia podľa design/_app.js (V.settings): kľúč, katalóg, spotreba limitu, posledné behy. */
export function SettingsPage() {
  const status = useWordfenceStatus();
  const sync = useSyncFeed();
  const reset = useResetBreaker();
  const replay = useReplayWebhookEvent();
  const toast = useToast();

  async function runSync(feed: Feed) {
    try {
      const run = await sync.mutateAsync(feed);
      toast(run.status === 'SUCCEEDED'
        ? `${FEED_LABEL[feed]}: ${nf.format(run.records ?? 0)} záznamov, ${nf.format(run.changed ?? 0)} zmenených`
        : `Sync zlyhal: ${run.error ?? 'neznáma chyba'}`);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Sync sa nepodarilo spustiť');
    }
  }

  async function resetBreaker() {
    await reset.mutateAsync();
    toast('Breaker zatvorený — ďalší sync sa pokúsi o kľúč znova');
  }

  async function replayEvent(id: string) {
    try {
      const { event, result } = await replay.mutateAsync(id);
      toast(event.status === 'PROCESSED' && result
        ? `Event prehratý: ${result.applied} zapísaných, ${result.unchanged} bez zmeny, ${result.stale} starších${result.rejected ? `, ${result.rejected} odmietnutých` : ''}`
        : `Prehratie zlyhalo: ${event.error ?? 'neznáma chyba'}`);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Event sa nepodarilo prehrať');
    }
  }

  if (status.isPending) return <PageTitle title="Nastavenia" sub="Wordfence, webhook a limity" />;
  if (status.isError || !status.data) {
    return (
      <>
        <PageTitle title="Nastavenia" sub="Wordfence, webhook a limity" />
        <div className="card p-6 text-sev-critical">Stav Wordfence sa nepodarilo načítať: {status.error?.message}</div>
      </>
    );
  }
  const s = status.data;
  const state = catalogState(s);
  const disabled = s.canSync !== null || sync.isPending;

  return (
    <>
      <PageTitle title="Nastavenia" sub="Wordfence, webhook a limity" />
      <div className="stack max-w-[820px]">
        <KeyCard s={s} onReset={resetBreaker} resetting={reset.isPending} />
        <CatalogCard s={s} tone={state.tone} label={state.label} />

        <div className="card flex flex-col gap-4 p-[22px]">
          <div className="flex items-baseline justify-between">
            <div className="card-t">Spotreba limitu</div>
            <span className="mono text-xs text-faint">{s.key.usedToday} / {s.key.cap} dnes</span>
          </div>
          <div className="flex h-[26px] gap-[2px]" role="img" aria-label={`Minutých ${s.key.usedToday} zo ${s.key.cap} requestov; 4 ostávajú ako rezerva`}>
            {quotaSlots(s.key.usedToday, s.key.cap).map((slot, i) => (
              <span
                key={i}
                className={`flex-1 rounded-[1px] ${slot === 'used' ? 'bg-brand' : slot === 'free' ? 'bg-raised' : 'border border-dashed border-line-2'}`}
              />
            ))}
          </div>
          <div className="flex flex-col">
            <Row k="Production feed" v={`1× denne · ${formatUtcTimes(s.schedule.production)}`} />
            <Row k="Scanner feed" v={`${s.schedule.scanner.length}× denne · ${formatUtcTimes(s.schedule.scanner)}`} />
            <Row k="Zostatok na dnes" v={`${s.key.remainingToday} z ${s.key.cap} requestov`} />
            <Row k="Ďalšie okno" v={nextWindow(s)} />
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <button type="button" className="btn btn-p" onClick={() => runSync('PRODUCTION')} disabled={disabled} title={s.canSync?.message}>
              <Icon name="sync" size={15} />
              {sync.isPending && sync.variables === 'PRODUCTION' ? 'Synchronizujem…' : 'Synchronizovať production'}
            </button>
            <button type="button" className="btn btn-g" onClick={() => runSync('SCANNER')} disabled={disabled} title={s.canSync?.message}>
              <Icon name="sync" size={15} />
              {sync.isPending && sync.variables === 'SCANNER' ? 'Synchronizujem…' : 'Synchronizovať scanner'}
            </button>
          </div>
          {s.canSync && (
            <span className="note border-sev-medium/30 bg-sev-medium/10 text-[12.5px]" role="status">
              <Icon name="lock" size={15} className="mt-px shrink-0 text-sev-medium" />
              <span>{s.canSync.message}</span>
            </span>
          )}
          {!s.canSync && (
            <span className="hint">Každý sync minie jeden z {s.key.cap} denných requestov — slot sa spotrebuje aj keď stiahnutie zlyhá.</span>
          )}
        </div>

        <RunsCard runs={s.runs} />
        <WebhookCard w={s.webhook} onReplay={replayEvent} replayingId={replay.isPending ? replay.variables : null} />
      </div>
    </>
  );
}

function nextWindow(s: WordfenceStatus): string {
  if (!s.key.windowOpensAt) return 'otvorené';
  const t = new Date(s.key.windowOpensAt).getTime();
  return t <= Date.now() ? 'otvorené' : `${formatDateTime(s.key.windowOpensAt)} (${timeAgo(s.key.windowOpensAt)})`;
}

function Row({ k, v, tone }: { k: string; v: string; tone?: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-[#242424] py-[9px] last:border-b-0">
      <span className="text-[12.5px] text-dim">{k}</span>
      <span className={`mono text-right text-xs ${tone ?? 'text-faint'}`}>{v}</span>
    </div>
  );
}

function KeyCard({ s, onReset, resetting }: { s: WordfenceStatus; onReset: () => void; resetting: boolean }) {
  return (
    <div className="card flex flex-col gap-[18px] p-[22px]">
      <div>
        <div className="card-t">Wordfence Intelligence</div>
        <div className="hint mt-[5px]">Jeden kľúč pre celú inštanciu. Katalóg je zdieľaný — pridanie stránky nespotrebuje ani jeden request navyše.</div>
      </div>
      <div className="field">
        <span>API kľúč</span>
        <div className="fbox">
          {s.key.configured ? (
            <>
              <span className="mono text-dim">wfi_••••••••••••••••••••••••</span>
              <span className="pill ml-auto bg-ok/15 text-ok">nastavený v prostredí</span>
            </>
          ) : (
            <>
              <span className="mono text-dim">—</span>
              <span className="pill ml-auto bg-sev-high/15 text-sev-high">nie je nastavený</span>
            </>
          )}
        </div>
        <span className="hint">
          Vytvorí sa v účte na wordfence.com → Integrations a nastaví sa ako <span className="mono">WORDFENCE_API_KEY</span> v prostredí servera — do databázy sa neukladá.
        </span>
      </div>
      <div className="field">
        <span>Webhook</span>
        <WebhookBox w={s.webhook} />
        <span className="hint">
          Wordfence posiela nové zraniteľnosti okamžite, mimo denného limitu. Secret žije ako <span className="mono">WORDFENCE_WEBHOOK_SECRET</span> v prostredí;
          URL pre Wordfence: <span className="mono">{`${window.location.origin}/api/wordfence/webhook`}</span>.
        </span>
      </div>
      {s.key.breakerOpen && (
        <div className="note border-sev-critical/30 bg-sev-critical/10 items-center gap-3">
          <Icon name="warn" size={16} className="shrink-0 text-sev-critical" />
          <span className="flex-1 text-[12.5px]">
            Breaker je otvorený: {s.key.breakerReason}. Sync sa nespustí, kým ho po oprave kľúča neresetuješ.
          </span>
          <button type="button" className="btn btn-g h-7 text-[11.5px]" onClick={onReset} disabled={resetting}>Resetovať breaker</button>
        </div>
      )}
    </div>
  );
}

function CatalogCard({ s, tone, label }: { s: WordfenceStatus; tone: CatalogTone; label: string }) {
  return (
    <div className="card flex flex-col gap-4 p-[22px]">
      <div className="flex items-center justify-between gap-4">
        <span className="card-t">Katalóg</span>
        <span className="flex items-center gap-2 text-xs text-dim">
          <span className={`dotst ${TONE[tone]}`} />
          {label}
        </span>
      </div>
      <div>
        <div className="mono text-[32px] font-semibold leading-none tracking-[-1px]">{nf.format(s.catalog.total)}</div>
        <div className="mt-2 text-xs text-faint">
          zraniteľností v katalógu · {s.lastProductionSyncAt ? `production sync ${timeAgo(s.lastProductionSyncAt)}` : 'production feed ešte nebol stiahnutý'}
        </div>
      </div>
      <div className="flex flex-col border-t border-line pt-2">
        <Row k="Z production feedu (s CVSS)" v={nf.format(s.catalog.production)} />
        <Row k="Len zo scanner feedu — nepotvrdené, bez CVSS" v={nf.format(s.catalog.scannerOnly)} tone={s.catalog.scannerOnly ? 'text-sev-unconfirmed' : undefined} />
        <Row k="Informational (netvoria nálezy)" v={nf.format(s.catalog.informational)} />
        <Row k="Rozsahov verzií" v={nf.format(s.catalog.ranges)} />
        <Row k="Stiahnuté z oboch feedov" v={nf.format(s.catalog.deleted)} />
        <Row k="Posledný production sync" v={s.lastProductionSyncAt ? `${formatDateTime(s.lastProductionSyncAt)} · ${timeAgo(s.lastProductionSyncAt)}` : 'nikdy'} />
        <Row k="Posledný scanner sync" v={s.lastScannerSyncAt ? `${formatDateTime(s.lastScannerSyncAt)} · ${timeAgo(s.lastScannerSyncAt)}` : 'nikdy'} />
      </div>
    </div>
  );
}

function RunsCard({ runs }: { runs: FeedSyncRunView[] }) {
  return (
    <div className="card">
      <div className="card-h"><span className="card-t">Posledné behy</span><span className="hint">FeedSyncRun — každý minul jeden request</span></div>
      {runs.length === 0 ? (
        <div className="empty"><Icon name="info" size={24} />Zatiaľ žiadny sync.</div>
      ) : (
        <div className="tblwrap">
          <table className="tbl">
            <thead>
              <tr><th>Začiatok</th><th>Feed</th><th>Spúšťač</th><th>Stav</th><th>Záznamy</th><th>Zmenené</th><th>HTTP</th></tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id}>
                  <td className="mono text-xs">{formatDateTime(r.startedAt)}</td>
                  <td>{FEED_LABEL[r.feed]}</td>
                  <td className="text-dim">{TRIGGER_LABEL[r.trigger]}</td>
                  <td>
                    {r.status === 'SUCCEEDED' && <span className="pill bg-ok/15 text-ok">úspech</span>}
                    {r.status === 'RUNNING' && <span className="pill bg-raised text-dim">beží</span>}
                    {r.status === 'FAILED' && (
                      <span className="flex flex-col gap-1">
                        <span className="pill w-fit bg-sev-critical/15 text-sev-critical">zlyhal</span>
                        {r.error && <span className="sub max-w-[40ch] whitespace-normal">{r.error}</span>}
                      </span>
                    )}
                  </td>
                  <td className="mono text-xs">{r.records === null ? '—' : nf.format(r.records)}</td>
                  <td className="mono text-xs">{r.changed === null ? '—' : nf.format(r.changed)}</td>
                  <td className="mono text-xs">{r.httpStatus ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const EVENT_STATUS: Record<WebhookEventView['status'], { label: string; cls: string }> = {
  RECEIVED: { label: 'čaká', cls: 'bg-raised text-dim' },
  PROCESSED: { label: 'spracovaný', cls: 'bg-ok/15 text-ok' },
  FAILED: { label: 'zlyhal', cls: 'bg-sev-critical/15 text-sev-critical' },
};

function WebhookBox({ w }: { w: WebhookStatus }) {
  const state = webhookState(w);
  return (
    <div className="fbox">
      <span className="mono text-dim">
        {!w.configured ? '—' : w.lastSeenAt ? `posledný event ${timeAgo(w.lastSeenAt)}` : 'zatiaľ žiadny event'}
      </span>
      <span className={`pill ml-auto ${state.tone === 'ok' ? 'bg-ok/15 text-ok' : state.tone === 'bad' ? 'bg-sev-high/15 text-sev-high' : state.tone === 'stale' ? 'bg-sev-medium/15 text-sev-medium' : 'bg-raised text-faint'}`}>
        {state.label}
      </span>
    </div>
  );
}

/** Posledné prijaté eventy. Prehratie ide tou istou cestou ako živý event — takto sa cesta overí bez čakania na Wordfence. */
function WebhookCard({ w, onReplay, replayingId }: { w: WebhookStatus; onReplay: (id: string) => void; replayingId: string | null | undefined }) {
  return (
    <div className="card">
      <div className="card-h">
        <span className="card-t">Webhook eventy</span>
        <span className="hint">{w.processed} spracovaných · {w.pending} čaká · {w.failed} chybných</span>
      </div>
      {w.events.length === 0 ? (
        <div className="empty">
          <Icon name="info" size={24} />
          {w.configured ? 'Zatiaľ žiadny event. Uložený event sa dá kedykoľvek prehrať.' : 'Bez secretu v prostredí webhook nič neprijíma (503).'}
        </div>
      ) : (
        <div className="tblwrap">
          <table className="tbl">
            <thead>
              <tr><th>Prijatý</th><th>Záznam</th><th>Stav</th><th>Spracovaný</th><th></th></tr>
            </thead>
            <tbody>
              {w.events.map((e) => (
                <tr key={e.id}>
                  <td className="mono text-xs">{formatDateTime(e.receivedAt)}</td>
                  <td>
                    <span className="flex flex-col gap-0.5">
                      <span className="max-w-[44ch] truncate" title={e.title ?? undefined}>{e.title ?? 'nerozpoznaný payload'}</span>
                      {e.records > 1 && <span className="sub">+{e.records - 1} ďalších</span>}
                    </span>
                  </td>
                  <td>
                    <span className="flex flex-col gap-1">
                      <span className={`pill w-fit ${EVENT_STATUS[e.status].cls}`}>{EVENT_STATUS[e.status].label}</span>
                      {e.error && <span className="sub max-w-[40ch] whitespace-normal">{e.error}</span>}
                    </span>
                  </td>
                  <td className="mono text-xs">{e.processedAt ? timeAgo(e.processedAt) : '—'}</td>
                  <td className="text-right">
                    <button type="button" className="btn btn-g h-7 text-[11.5px]" onClick={() => onReplay(e.id)} disabled={replayingId === e.id}>
                      <Icon name="sync" size={13} />
                      {replayingId === e.id ? 'Prehrávam…' : 'Prehrať'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
